class StickyAtcBar extends HTMLElement {
  connectedCallback() {
    if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      document.documentElement.classList.add('is-ios');
    }
    this._triggerSelector = this.dataset.triggerSelector || null;
    this._ticking = false;
    this._onScroll = this._handleScroll.bind(this);
    this._onResize = this._positionBelowHeader.bind(this);
    this._onScrollPosition = this._handleScrollPosition.bind(this);

    this._footerEl = document.querySelector('footer.footer');

    this._positionBelowHeader();
    this._watchStickyHeader();
    window.addEventListener('resize', this._onResize, { passive: true });
    window.addEventListener('scroll', this._onScrollPosition, { passive: true });

    // ATC sync is always initialized regardless of scroll trigger type
    this._initAtcSync();

    if (this._triggerSelector) {
      const triggerEl = document.querySelector(this._triggerSelector);
      if (triggerEl) {
        this._visibilityObserver = new IntersectionObserver(
          ([entry]) => this._setVisible(!entry.isIntersecting),
          { threshold: 0, rootMargin: '0px' }
        );
        this._visibilityObserver.observe(triggerEl);
        return;
      }
    }

    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('scroll', this._onScrollPosition);
    window.removeEventListener('resize', this._onResize);
    if (this._visibilityObserver) this._visibilityObserver.disconnect();
    if (this._atcObserver) this._atcObserver.disconnect();
    if (this._headerObserver) this._headerObserver.disconnect();
    if (this._headerResizeObserver) this._headerResizeObserver.disconnect();
  }

  // ── Scroll / visibility ───────────────────────────────────────────────────

  _handleScrollPosition() {
    this._positionBelowHeader();
    const show = this._intendedVisible && !this._isFooterDominatingViewport();
    if (show) this.style.top = `${this._headerBottom || 0}px`;
    this.toggleAttribute('data-visible', show);
  }

  _positionBelowHeader() {
    const stickyHeader = document.querySelector('sticky-header');
    this._headerBottom = stickyHeader ? Math.max(0, stickyHeader.getBoundingClientRect().bottom) : 0;
    if (this.hasAttribute('data-visible')) {
      this.style.top = `${this._headerBottom}px`;
    }
  }

  _isFooterDominatingViewport() {
    if (!this._footerEl) return false;
    const rect = this._footerEl.getBoundingClientRect();
    // Footer top at or above viewport top = footer fills most/all of screen
    return rect.top <= 0;
  }

  _setVisible(visible) {
    this._intendedVisible = visible;
    const show = visible && !this._isFooterDominatingViewport();
    if (show) {
      this.style.top = `${this._headerBottom || 0}px`;
    }
    this.toggleAttribute('data-visible', show);
  }

  _watchStickyHeader() {
    const stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader) return;

    this._headerObserver = new MutationObserver(() => this._positionBelowHeader());
    this._headerObserver.observe(stickyHeader, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    this._headerResizeObserver = new ResizeObserver(() => this._positionBelowHeader());
    this._headerResizeObserver.observe(stickyHeader);
  }

  _handleScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._setVisible(window.scrollY >= window.innerHeight);
      this._ticking = false;
    });
  }

  // ── ATC sync ──────────────────────────────────────────────────────────────

  _initAtcSync() {
    this._stickyAtcBtns = Array.from(this.querySelectorAll('[data-sticky-atc]'));
    if (!this._stickyAtcBtns.length) return;

    // Target the main product form's submit button
    const mainBtn =
      document.querySelector('product-form [name="add"]') ||
      document.querySelector('[data-type="add-to-cart-form"] [name="add"]');

    if (!mainBtn) return;
    this._mainAtcBtn = mainBtn;

    // Sync once immediately, then again after a tick so variant JS can settle
    this._syncAtcState();
    requestAnimationFrame(() => this._syncAtcState());

    // Keep in sync as the main button changes (variant selection, sold out, loading)
    this._atcObserver = new MutationObserver(() => this._syncAtcState());
    this._atcObserver.observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Click handler: proxy to main button, or scroll to picker when no size selected
    this._stickyAtcBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const state = btn.dataset.atcState;
        if (state === 'ready') {
          this._mainAtcBtn.click();
        } else if (state === 'prompt') {
          this._scrollToVariantPicker();
        }
        // 'sold-out' and 'loading' have pointer-events: none — won't reach here
      });
    });
  }

  _syncAtcState() {
    const mainBtn = this._mainAtcBtn;
    const isDisabled = mainBtn.hasAttribute('disabled');
    const isLoading = mainBtn.getAttribute('aria-disabled') === 'true';
    const textEl = mainBtn.querySelector('span');
    const text = textEl ? textEl.textContent.trim() : mainBtn.textContent.trim();

    this._stickyAtcBtns.forEach((btn) => {
      let state = 'ready';
      if (isLoading) {
        state = 'loading';
      } else if (isDisabled) {
        state = this._isPromptState() ? 'prompt' : 'sold-out';
      }
      btn.dataset.atcState = state;

      // Never use btn.disabled — it blocks click events. Use aria-disabled + CSS instead.
      btn.disabled = false;
      btn.setAttribute('aria-disabled', String(isLoading || (isDisabled && state === 'sold-out')));

      const stickyTextEl = btn.querySelector('[data-sticky-atc-text]');
      if (stickyTextEl && text) stickyTextEl.textContent = text;
    });
  }

  // Returns true when the product has no variant selected yet (vs sold-out which has one)
  _isPromptState() {
    const picker = document.querySelector('variant-selects, variant-radios');
    if (!picker) return false;

    // Dropdown pickers: Dawn uses value="disabled" as the unselected placeholder
    const selects = picker.querySelectorAll('select');
    for (const sel of selects) {
      if (!sel.value || sel.value === 'disabled') return true;
    }

    // Radio pickers: check for any fieldset with no checked option
    const fieldsets = picker.querySelectorAll('fieldset');
    for (const fs of fieldsets) {
      if (!fs.querySelector('input:checked')) return true;
    }

    return false;
  }

  _scrollToVariantPicker() {
    const picker = document.querySelector('variant-radios, variant-selects');
    const target = picker || document.querySelector('.product__info-wrapper');
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('atc-prompt-highlight');
    target.addEventListener('animationend', () => target.classList.remove('atc-prompt-highlight'), { once: true });
  }
}

customElements.define('sticky-atc-bar', StickyAtcBar);
